import React, { lazy, Suspense } from 'react';

interface Opts {
  fallback: React.ReactNode;
}
type Unpromisify<T> = T extends Promise<infer P> ? P : never;

export const lazyLoad = <T extends Promise<any>, U extends React.ComponentType<any>>(
  importFunc: () => T,
  selectorFunc?: (s: Unpromisify<T>) => U,
  opts: Opts = { fallback: null },
) => {
  //@ts-ignore
  let lazyFactory: () => Promise<{ default: U }> = retry(importFunc);

  if (selectorFunc) {
    lazyFactory = () => retry(importFunc).then(module => ({ default: selectorFunc(module as Unpromisify<T>) }));
  }

  const LazyComponent = lazy(lazyFactory);

  return (props: React.ComponentProps<U>): JSX.Element => (
    <Suspense fallback={opts.fallback!}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Enhanced retry function with special handling for chunk loading errors
function retry(fn, retriesLeft = 5, interval = 1000) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch(error => {
        // Check if this is a ChunkLoadError or network timeout
        const isChunkError =
          error?.name === 'ChunkLoadError' ||
          error?.message?.includes('Loading chunk') ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('failed');

        // For chunk errors, increase retry count and use longer intervals
        const maxRetries = isChunkError ? 10 : 5;
        const retryInterval = isChunkError ? Math.min(2000 * (maxRetries - retriesLeft + 1), 10000) : interval;

        if (retriesLeft === 1) {
          console.error('Maximum retries exceeded for chunk loading:', error);

          // For chunk errors, try one final attempt with cache busting
          if (isChunkError) {
            console.warn('Attempting final retry with cache bust...');
            return clearCacheAndRetry(fn).then(resolve).catch(reject);
          }

          reject(error);
          return;
        }

        console.warn(
          `Retrying chunk load (${maxRetries - retriesLeft + 1}/${maxRetries}) in ${retryInterval}ms:`,
          error?.message,
        );

        setTimeout(() => {
          // For chunk errors, try cache busting on every other retry
          if (isChunkError && (maxRetries - retriesLeft) % 2 === 0) {
            clearCacheAndRetry(fn)
              .then(resolve)
              .catch(() => retry(fn, retriesLeft - 1, retryInterval).then(resolve, reject));
          } else {
            retry(fn, retriesLeft - 1, retryInterval).then(resolve, reject);
          }
        }, retryInterval);
      });
  });
}

// Cache busting utility for chunk loading failures
function clearCacheAndRetry(fn) {
  return new Promise((resolve, reject) => {
    // Clear any cached modules that might be stale
    if (typeof window !== 'undefined' && window.__webpack_require__) {
      // Clear webpack module cache for failed chunks
      const webpackRequire = window.__webpack_require__;
      if (webpackRequire.cache) {
        Object.keys(webpackRequire.cache).forEach(key => {
          if (key.includes('chunk')) {
            delete webpackRequire.cache[key];
          }
        });
      }
    }

    // Add cache-busting timestamp to imports
    const originalFetch = window.fetch;
    const cacheBustingFetch = (input, init) => {
      if (typeof input === 'string' && (input.includes('.js') || input.includes('chunk'))) {
        const url = new URL(input, window.location.origin);
        url.searchParams.set('_cb', Date.now().toString());
        return originalFetch(url.toString(), init);
      }
      return originalFetch(input, init);
    };

    // Temporarily replace fetch with cache-busting version
    window.fetch = cacheBustingFetch;

    fn()
      .then(result => {
        window.fetch = originalFetch; // Restore original fetch
        resolve(result);
      })
      .catch(error => {
        window.fetch = originalFetch; // Restore original fetch
        reject(error);
      });
  });
}
